import { z } from 'zod';
import { IsoDateTimeSchema, SegmentSchema, UuidSchema } from './common.js';

// Uhrzeit HH:MM im 24h-Format (Lokalzeit, siehe sync-schedule/evaluate.ts).
export const TimeOfDaySchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Zeit muss HH:MM (24h) sein');
export type TimeOfDay = z.infer<typeof TimeOfDaySchema>;

// Wochentag nach JS Date.getDay(): 0=So, 1=Mo … 6=Sa.
export const WeekdaySchema = z.number().int().min(0).max(6);
export type Weekday = z.infer<typeof WeekdaySchema>;

// Erlaubte Intervalle im Zeitfenster. Bewusst diskret (UI-Dropdown) und so
// gewählt, dass auch 45/90/120 Min sauber abbildbar sind (Cron kann das nicht).
export const EveryMinutesSchema = z.union([
  z.literal(10),
  z.literal(20),
  z.literal(30),
  z.literal(45),
  z.literal(60),
  z.literal(90),
  z.literal(120),
]);
export type EveryMinutes = z.infer<typeof EveryMinutesSchema>;

// Stapelbare Zeitplan-Regeln. Mehrere Regeln gelten additiv (ODER-verknüpft).
export const SyncRuleSchema = z.discriminatedUnion('type', [
  // Täglich um eine oder mehrere Uhrzeiten ("täglich um X" / "X und Y").
  z.object({
    type: z.literal('daily_at'),
    times: z.array(TimeOfDaySchema).min(1).max(24),
  }),
  // Zwischen from und to alle N Minuten, optional auf Wochentage beschränkt.
  z.object({
    type: z.literal('interval_window'),
    from: TimeOfDaySchema,
    to: TimeOfDaySchema,
    everyMinutes: EveryMinutesSchema,
    days: z.array(WeekdaySchema).min(1).max(7).optional(),
  }),
  // Alle N Stunden, täglich verankert ab anchor (Default 00:00).
  z.object({
    type: z.literal('every_n_hours'),
    n: z.number().int().min(1).max(24),
    anchor: TimeOfDaySchema.optional(),
  }),
  // Pro Wochentag eine eigene Uhrzeit (Mo 10:00 · Di 10:30 …).
  z.object({
    type: z.literal('weekday_at'),
    entries: z
      .array(z.object({ weekday: WeekdaySchema, time: TimeOfDaySchema }))
      .min(1)
      .max(50),
  }),
]);
export type SyncRule = z.infer<typeof SyncRuleSchema>;

// Vom Admin pflegbarer Zeitplan (in DB: sync_schedule.rules + enabled + segment_whitelist).
// Auch als Schedule-Update-Body (PUT /schedule) verwendet.
export const SyncScheduleSchema = z.object({
  enabled: z.boolean(),
  rules: z.array(SyncRuleSchema).max(50),
  // NULL / fehlendes Feld / leeres Array = alle Segmente (Bestandsschutz). Migration 0027.
  segment_whitelist: z.array(SegmentSchema).optional(),
  // Zweiter Zeitplan: Re-Sync bestehender Projekte (LastModifiedDate). Migration 0037.
  // Defaults sichern Rückwärtskompatibilität für PUT-Bodies ohne diese Felder.
  refresh_enabled: z.boolean().default(false),
  refresh_rules: z.array(SyncRuleSchema).max(50).default([]),
});
export type SyncSchedule = z.infer<typeof SyncScheduleSchema>;

// Antwort von GET /schedule: Zeitplan plus serverseitige Metadaten.
export const SyncScheduleStateSchema = SyncScheduleSchema.extend({
  last_auto_run_at: IsoDateTimeSchema.nullable(),
  // Delta-Wasserzeichen (CreatedDate >). null = nächster Lauf zieht voll.
  delta_watermark: IsoDateTimeSchema.nullable(),
  // Refresh-Zeitplan-Metadaten. Kein Wasserzeichen mehr (#47): der Refresh
  // fragt gezielt die offenen Projekte ab (`projectKeys`), statt über ein
  // Änderungs-Zeitfenster den kompletten SF-Bestand zu ziehen.
  refresh_last_auto_run_at: IsoDateTimeSchema.nullable(),
  updated_at: IsoDateTimeSchema.nullable(),
  updated_by: UuidSchema.nullable(),
});
export type SyncScheduleState = z.infer<typeof SyncScheduleStateSchema>;

// Delta-Wasserzeichen setzen ("ab Datum") oder löschen (null → Voll-Lauf).
export const WatermarkInputSchema = z.object({
  delta_watermark: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum muss YYYY-MM-DD sein')
    .nullable(),
});
export type WatermarkInput = z.infer<typeof WatermarkInputSchema>;

// Manuelles Re-Sync per Salesforce-Id (15- oder 18-stellig, alphanumerisch).
export const ManualResyncSchema = z.object({
  salesforce_ids: z
    .array(z.string().regex(/^[a-zA-Z0-9]{15,18}$/, 'ungültige Salesforce-Id'))
    .min(1)
    .max(50),
});
export type ManualResyncInput = z.infer<typeof ManualResyncSchema>;

// Manueller Sync-Trigger-Body (POST /v1/sync-runs/trigger).
// `segments` überschreibt pro Lauf die gespeicherte segment_whitelist des Schedules.
// Fehlt das Feld → Fallback auf syncSchedule.segment_whitelist (= heutiges Verhalten).
export const SyncTriggerBodySchema = z.object({
  since_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'since_date muss YYYY-MM-DD sein')
    .optional(),
  // Diesen Lauf bei Erfolg als neues Delta-Wasserzeichen übernehmen (UI-Checkbox).
  set_watermark: z.boolean().optional(),
  // Segment-Override: überschreibt die gespeicherte Whitelist für diesen Lauf.
  // Fehlt / leer = Fallback auf syncSchedule.segment_whitelist.
  segments: z.array(SegmentSchema).optional(),
});
export type SyncTriggerBody = z.infer<typeof SyncTriggerBodySchema>;
